// What a Scout reports about its own software, read for the admin device page.
//
// Two separate parts of the Scout talk to us. The heartbeat (devices.last_seen_at)
// says the Scout is online. The update check is a different report, sent by the
// launcher (UpdateTelemetry.kt in vortexnav-client) to /telemetry/update-logs
// and kept in device_update_logs: one row per check, with every app's installed
// version and the version the update server offered at that moment. A Scout can
// send heartbeats for months without ever sending an update check, and then it
// is probably not receiving updates either. Showing the two side by side is the
// point of the Software card.

/** A Scout app as the Scout's own Updates dialog names it. */
export interface ScoutApp {
  pkg: string;
  name: string;
  /** Shown beside the name for staff, who know some apps by another word. */
  hint?: string;
}

// Names match appDisplayNames in the launcher's LauncherViewModel.kt, so staff
// and the skipper on the phone see the same words. PolarisOS leads because the
// rest of the Scout depends on it; the others follow the dialog's order.
export const SCOUT_APPS: readonly ScoutApp[] = [
  { pkg: 'com.vortexmarine.launcher', name: 'PolarisOS', hint: 'Launcher' },
  { pkg: 'com.vortexmarine.vortexnav', name: 'VortexNav' },
  { pkg: 'com.vortexmarine.truehelm', name: 'TrueHelm' },
  { pkg: 'com.vortexmarine.vortextide', name: 'VortexTide' },
  { pkg: 'com.vortexmarine.vortexanchor', name: 'VortexAnchor' },
  { pkg: 'com.vortexmarine.vortexcompass', name: 'VortexCompass' },
  { pkg: 'com.vortexmarine.webview', name: 'System Services', hint: 'WebView' },
  // Retired (Tony, 2026-08-03): still installed and still reported, never updated.
  { pkg: 'com.vortexmarine.gpsdiag', name: 'GPS Diag', hint: 'Retired' },
];

/**
 * A Scout Gateway is stored with device_type 'scout' but is a different box: it
 * never sends launcher update checks, and has its own software card. The serial
 * pattern is the one My Devices uses to tell them apart.
 */
const GATEWAY_SERIAL = /^SG-[0-9A-F]{16}$/;

export function isScoutTablet(device: { device_type?: string | null; serial_number?: string | null }): boolean {
  return device.device_type === 'scout' && !GATEWAY_SERIAL.test(device.serial_number ?? '');
}

/** current: on (or ahead of) the offer · update: the offer is newer · unknown: cannot tell. */
export type AppVersionState = 'current' | 'update' | 'unknown';

export interface AppVersion extends ScoutApp {
  installed: string | null;
  installedCode: number | null;
  offered: string | null;
  offeredCode: number | null;
  state: AppVersionState;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function code(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return null;
}

function stateOf(installed: string | null, installedCode: number | null, offered: string | null, offeredCode: number | null): AppVersionState {
  // The launcher's own `action` field reads "none" even when an update waits,
  // so the version codes are the only reliable comparison.
  if (installedCode != null && offeredCode != null) return installedCode < offeredCode ? 'update' : 'current';
  if (installed != null && installed === offered) return 'current';
  return 'unknown';
}

/** Every app in one update check, known apps in SCOUT_APPS order, then the rest. */
export function appVersions(appDetails: unknown): AppVersion[] {
  if (!Array.isArray(appDetails)) return [];

  const rows: AppVersion[] = [];
  for (const item of appDetails) {
    if (!item || typeof item !== 'object') continue;
    const e = item as Record<string, unknown>;
    const pkg = text(e.package);
    if (!pkg) continue;

    const app = SCOUT_APPS.find((a) => a.pkg === pkg) ?? { pkg, name: pkg };
    const installed = text(e.installed_version);
    const installedCode = code(e.installed_version_code);
    const offered = text(e.available_version);
    const offeredCode = code(e.available_version_code);
    rows.push({ ...app, installed, installedCode, offered, offeredCode, state: stateOf(installed, installedCode, offered, offeredCode) });
  }

  const rank = (pkg: string) => {
    const i = SCOUT_APPS.findIndex((a) => a.pkg === pkg);
    return i === -1 ? SCOUT_APPS.length : i;
  };
  return rows.sort((a, b) => rank(a.pkg) - rank(b.pkg) || a.pkg.localeCompare(b.pkg));
}

/**
 * How long a Scout may keep sending heartbeats after its last update check
 * before we say it has stopped checking. Measured on the fleet on 8 Oct 2026:
 * healthy Scouts' last check sat within 9 days of their last heartbeat; the
 * next gap up was 24 days, and stuck Scouts ran 24 to 145.
 */
export const UPDATE_CHECK_SILENCE_DAYS = 14;

export type UpdateCheckHealth =
  | { kind: 'ok' }
  /** No heartbeat and no check, ever: switched on and connected never happened. */
  | { kind: 'never-connected' }
  /** Online at some point, but not one update check has reached us. */
  | { kind: 'never-checked'; lastSeenAt: Date }
  /** Still sending heartbeats well after its last update check. */
  | { kind: 'silent'; lastCheckAt: Date; lastSeenAt: Date; days: number };

// "2026-10-07 22:59:38.123456+00": Postgres text, which drizzle-orm/neon-http
// passes through untouched for timestamptz columns in string mode. No zone
// means UTC, the server's own zone.
const PG_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}(?::?\d{2})?)?$/;

/**
 * Parses a timestamp from the admin API. Rewrites the Postgres text form as
 * strict ISO first: Chrome reads "2026-10-07 22:59:38+00" as is, but Safari's
 * Date parser does not. Returns null for anything unreadable.
 */
export function parseTimestamp(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  let text = value.trim();
  const m = PG_TIMESTAMP.exec(text);
  if (m) {
    const [, date, time, fraction, zone] = m;
    const ms = fraction ? `.${fraction.padEnd(3, '0').slice(0, 3)}` : '';
    const offset = !zone || zone === 'Z' ? 'Z' : `${zone.slice(0, 3)}:${zone.slice(3).replace(':', '') || '00'}`;
    text = `${date}T${time}${ms}${offset}`;
  }
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}

function when(value: Date | string | null | undefined): Date | null {
  return value instanceof Date ? value : parseTimestamp(value);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** One update check as /api/admin/neptune/updates returns it (fields used here). */
export interface UpdateCheckRow {
  createdAt?: unknown;
  checkedAt?: unknown;
  result?: unknown;
  errorMessage?: unknown;
  appDetails?: unknown;
  deviceMeta?: unknown;
}

/**
 * When the check reached us. checked_at is stamped by the Scout's own clock
 * (UpdateTelemetry.kt sends isoNow()), which can be off before a GPS fix;
 * created_at is our server's, so it wins when present.
 */
export function checkReceivedAt(row: UpdateCheckRow | null | undefined): Date | null {
  if (!row) return null;
  return parseTimestamp(row.createdAt) ?? parseTimestamp(row.checkedAt);
}

/**
 * True once a check is too old for its "offered" column to describe today's
 * offer, e.g. a Scout switched off for weeks. Statuses are then worded in the past.
 */
export function isCheckOld(receivedAt: Date | null, now: Date = new Date()): boolean {
  return receivedAt != null && now.getTime() - receivedAt.getTime() >= UPDATE_CHECK_SILENCE_DAYS * DAY_MS;
}

/**
 * The newest check that listed any apps. A failed check usually lists none, and
 * the versions from the check before it are still the last known.
 */
export function latestWithVersions<T extends UpdateCheckRow>(rows: readonly T[] | null | undefined): T | null {
  return rows?.find((r) => Array.isArray(r.appDetails) && r.appDetails.length > 0) ?? null;
}

export type SoftwareHeadline = { kind: 'waiting'; count: number } | { kind: 'current' };

/**
 * The one-line summary for the card header. "All up to date" only when every
 * app is known to be current; nothing when that cannot be said honestly.
 */
export function softwareHeadline(apps: readonly AppVersion[], checkFailed: boolean): SoftwareHeadline | null {
  if (checkFailed || apps.length === 0) return null;
  const count = apps.filter((a) => a.state === 'update').length;
  if (count > 0) return { kind: 'waiting', count };
  return apps.every((a) => a.state === 'current') ? { kind: 'current' } : null;
}

/**
 * Compares the last heartbeat with the last update check. A Scout that has
 * been switched off since its last check is fine: both times stop together.
 */
export function updateCheckHealth(lastSeenAt: string | null | undefined, lastCheckAt: Date | string | null | undefined): UpdateCheckHealth {
  const seen = when(lastSeenAt);
  const check = when(lastCheckAt);

  if (!check) return seen ? { kind: 'never-checked', lastSeenAt: seen } : { kind: 'never-connected' };
  if (!seen) return { kind: 'ok' };

  const gap = seen.getTime() - check.getTime();
  if (gap < UPDATE_CHECK_SILENCE_DAYS * DAY_MS) return { kind: 'ok' };
  return { kind: 'silent', lastCheckAt: check, lastSeenAt: seen, days: Math.floor(gap / DAY_MS) };
}

// ---- Against the live release ----
//
// The update server (ota-updater in vortexnav-client) serves one manifest per
// channel. A launcher built in the platform flavour asks for the platform
// channel (X-Update-Channel); everything else gets stable. Per-device overrides
// exist (DEVICE_CHANNELS KV) but on 8 Oct 2026 held only a smoke-test device.

export type UpdateChannel = 'stable' | 'platform';

/**
 * The Scout's channel, from the launcher's own report of how it is signed
 * (kiosk_state…is_platform_signed; the Live monitor reads the same flag).
 * Null when an older launcher did not say.
 */
export function updateChannel(deviceMeta: unknown): UpdateChannel | null {
  let found: boolean | null = null;
  (function find(o: unknown): void {
    if (found !== null || !o || typeof o !== 'object') return;
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
      if (k === 'is_platform_signed' && typeof v === 'boolean') { found = v; return; }
      find(v);
    }
  })(deviceMeta);
  return found === null ? null : found ? 'platform' : 'stable';
}

/** The live manifests as GET /api/admin/ota/live/manifests returns them. */
export type LiveManifests = Partial<Record<UpdateChannel, {
  apps?: Record<string, { pkg: string; versionName: string; versionCode: number }>;
} | null>>;

export interface LatestRelease {
  version: string;
  code: number;
}

/** What the update server offers this package on the Scout's channel right now. */
export function latestRelease(manifests: LiveManifests | null | undefined, channel: UpdateChannel | null, pkg: string): LatestRelease | null {
  const find = (ch: UpdateChannel) => Object.values(manifests?.[ch]?.apps ?? {}).find((a) => a.pkg === pkg);
  const hit = (channel ? find(channel) : undefined) ?? find('stable');
  return hit ? { version: hit.versionName, code: hit.versionCode } : null;
}

/**
 * current: on the latest release (or newer).
 * waiting: its last check offered an update it has not installed.
 * not-offered: the latest came out after its last check, so it has not seen it.
 */
export type ReleaseStatus = 'current' | 'waiting' | 'not-offered';

export function releaseStatus(app: Pick<AppVersion, 'installedCode' | 'offeredCode'>, latest: LatestRelease | null): ReleaseStatus | null {
  if (!latest || app.installedCode == null) return null;
  if (app.installedCode >= latest.code) return 'current';
  if (app.offeredCode != null && app.offeredCode > app.installedCode) return 'waiting';
  return 'not-offered';
}

// ---- Release dates ----

/** One row of GET /api/admin/ota/live/release-dates. */
export interface ReleaseDate {
  package: string;
  versionCode: number;
  versionName: string;
  firstOfferedAt: string;
}

/**
 * When a version was released, taken as the first time the update server
 * offered it to any Scout. Matches on package, code and name together: codes
 * have been reused (launcher 229 was a debug build before 2.49.28). Null for a
 * version never offered, e.g. one installed at the factory or by hand.
 */
export function releasedAt(
  dates: readonly ReleaseDate[] | null | undefined,
  pkg: string,
  versionCode: number | null,
  versionName: string | null,
): Date | null {
  if (!dates || versionCode == null || !versionName) return null;
  const hit = dates.find((d) => d.package === pkg && d.versionCode === versionCode && d.versionName === versionName);
  return hit ? parseTimestamp(hit.firstOfferedAt) : null;
}
